import React, { Fragment, Suspense } from "react";
import {
  HashRouter as Router,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";

import { FeatureFlagProvider } from "./services/featureFlags";
import Authentication from "./Core/Authentication";
import Loader from "./Core/Loader/Loader";
import SSOLanding from "./Components/SSOLanding/SSOLanding";
import Profile from "./Components/StudentDashboard/Profiles/Profile";
import ViewRecording from "./Components/Resources/Recordings/ViewRecording";
import ChatWidget from "./Components/ChatWidget";
import NativeComponent from "./Components/NativeComponents";
import SalesforceChat from "./Components/SalesforceChat";
import RaiseRequestModal from "./Components/ParentDashboard/RaiseRequestModal";
import UnderMaintenance from "./Components/ChatWidget/UnderMaintenance.jsx";

import useProtectedRoute from "./hooks/useProtectedRoute";
import usePaidAccessGuard from "./hooks/usePaidAccessGuard";
import RedirectToSandboxModal from "./Components/RedirectToSandboxModal";
import { auth, getUser } from "./utils/auth";
import {
  SandboxRenderer,
  DesktopHeader,
  Login,
  HeaderComponent,
  BatchRequestManagement,
  Dashboard,
  ParentDashboard,
  StudentSummary,
  BookDemo,
  ViewSchedule,
  ViewScheduleV2,
  Referrals,
  RedeemRewards,
  AchievementsIndex,
  PtmFeedBack,
  Referal,
  Resources,
  Subscriptions,
  Community,
  ShowAllProjects,
  EditProfile,
  ZoomMeet,
  LeaveZoom,
  AutoReschedule,
  AddClassV2,
  CancelAndReschedule,
  SlotSelectorPage,
  ChangeBatchSchedule,
  StudentCommunity,
  Error,
  PreviewAlchemyFile,
  ClassSummary,
  WeeklyReport,
  Notifications,
  ReschedulePTM,
  TeachersDayPostPage,
  WebinarPaymentStatusPage,
  BuyCredits,
  ConfirmDemoAttendance,
  LoopAction,
  FeatureFlagsDebug,
} from "./utils/appRoutes";

toast.configure();

const ProtectedRoute = () => {
  const { state, derived, handlers, productTour } = useProtectedRoute();
  const { showProfiles, profileData, errFetchUsers, loading } = state;
  const {
    mobileDevice,
    showSwitchProfile,
    showSidebar,
    showDesktopHeader,
    layoutClass,
    contentClass,
    buyCreditsClass,
    backgroundForPath,
  } = derived;
  const { setShowProfiles, setLoading } = handlers;
  const { tourIndex, skip, productTourStatus, onBoardingComplete } =
    productTour;

  return (
    <div style={{ background: backgroundForPath }}>
      {showProfiles && showSwitchProfile && (
        <Profile
          setShowProfiles={setShowProfiles}
          showProfiles={showProfiles}
          profileData={profileData}
          errFetchUsers={errFetchUsers}
          loading={loading}
          setLoading={setLoading}
        />
      )}

      <div className={layoutClass}>
        {showSidebar && (
          <HeaderComponent
            index={tourIndex}
            mobileDevice={mobileDevice}
            skip={skip}
            productTourStatus={productTourStatus}
            setShowProfiles={setShowProfiles}
            showSwitchProfile={showSwitchProfile}
          />
        )}

        <div className={contentClass}>
          {showDesktopHeader && (
            <DesktopHeader
              setShowProfiles={setShowProfiles}
              showProfiles={showProfiles}
              showSwitchProfile={showSwitchProfile}
            />
          )}
          <div className={buyCreditsClass}>
            <Suspense fallback={<Loader />}>
              <Switch>
                {/* MFE route — kill switch: REACT_APP_MFE_ENABLED */}
                <Route path={["/dashboard", "/sb"]}>
                  {process.env.REACT_APP_MFE_ENABLED === "true" || true ? (
                    <SandboxRenderer />
                  ) : (
                    <Dashboard
                      onBoardingComplete={onBoardingComplete}
                      mobileDevice={mobileDevice}
                      setShowProfiles={setShowProfiles}
                      showSwitchProfile={showSwitchProfile}
                    />
                  )}
                </Route>
                <Route path="/old-dashboard">
                  <Dashboard
                    onBoardingComplete={onBoardingComplete}
                    mobileDevice={mobileDevice}
                    setShowProfiles={setShowProfiles}
                    showSwitchProfile={showSwitchProfile}
                  />
                </Route>
                <Route path={"/chatwithus"}>
                  {window.ReactNativeWebView ? (
                    <UnderMaintenance />
                  ) : (
                    <ChatWidget />
                  )}
                </Route>
                <Route path="/loop">
                  <BatchRequestManagement />
                </Route>
                {window.ReactNativeWebView && (
                  <Route path={"/raise-request"}>
                    <RaiseRequestModal />
                  </Route>
                )}
                {window.ReactNativeWebView && (
                  <Route path={"/parent-dashboard"}>
                    <ParentDashboard />
                  </Route>
                )}
                {window.ReactNativeWebView && (
                  <Route path={"/student-summary"}>
                    <StudentSummary />
                  </Route>
                )}
                {window.ReactNativeWebView && (
                  <Route path={"/book-a-demo"}>
                    <BookDemo />
                  </Route>
                )}
                <Route path="/referrals/redeem-rewards">
                  <RedeemRewards />
                </Route>
                <Route path="/referrals">
                  <Referrals />
                </Route>
                <Route path={"/resources"}>
                  <Resources />
                </Route>
                <Route path="/edit-profile">
                  <EditProfile />
                </Route>
                <Route path="/subscriptions">
                  <Subscriptions />
                </Route>
                <Route path="/community/showAllProject/:apiId">
                  <ShowAllProjects />
                </Route>
                <Route path={"/community"}>
                  <Community profileData={profileData} />
                </Route>
                <Route path={"/viewSchedule"}>
                  <ViewScheduleV2 />
                </Route>
                <Route path={"/viewScheduleV2"}>
                  <ViewSchedule />
                </Route>
                <Route path={"/viewScheduleV2"}>
                  <ViewScheduleV2 />
                </Route>
                <Route path="/notifications">
                  <Notifications />
                </Route>
                <Route path={"/addRescheduleClass"}>
                  <AddClassV2 />
                </Route>
                <Route path={"/weeklyRecap/:uuid"}>
                  <WeeklyReport />
                </Route>
                <Route path={"/buy-credits"}>
                  <BuyCredits />
                </Route>
                <Redirect to="/dashboard" />
              </Switch>
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// LoginRoute — the public "/" entry point.
// Already authenticated  → redirect to the right dashboard.
// MFE enabled            → mount the embedded Sandbox login page (initialPath
//                          "/login"). __SANDBOX_IS_PLUGIN__ is set globally by
//                          the index.js preload, so Sandbox reports
//                          from=student-portal to cy-sso.
// MFE disabled (kill sw) → fall back to the legacy StudentPortal Login.
// ---------------------------------------------------------------------------
const LoginRoute = () => {
  if (auth()) {
    const user = getUser();
    return (
      <Redirect
        to={
          user?.hasOwnProperty("parentId") ? "/parent-dashboard" : "/dashboard"
        }
      />
    );
  }

  if (process.env.REACT_APP_MFE_ENABLED === "true" || true) {
    return <SandboxRenderer initialPath="/login" />;
  }

  return <Login />;
};

function App() {
  // Paid-access guard: reveals the sidebar for paid students and redirects unpaid
  // users off the portal dashboard back to Sandbox. No-op for non-students.
  usePaidAccessGuard();

  return (
    <Fragment>
      <Router>
        <FeatureFlagProvider>
          <Suspense fallback={<Loader />}>
            <Switch>
              <Route path="/sso-login" exact>
                <SSOLanding />
              </Route>
              <Route path="/" exact>
                <LoginRoute />
              </Route>
              <Route path="/class/:meetingId/:uId" component={ZoomMeet} exact />
              <Route path="/leaveZoom" component={LeaveZoom} exact />
              <Route path="/404" component={Error} exact />
              <Route path={"/viewRecording"} component={ViewRecording} exact />
              <Route
                path="/autoReschedule/:scheduleId/:studentId"
                component={AutoReschedule}
                exact
              />
              <Route
                path={"/cancel/reschedule/:studentId"}
                component={CancelAndReschedule}
                exact
              />
              <Route
                path={"/cancel-reschedule/:studentId/select-slot"}
                component={SlotSelectorPage}
                exact
              />
              <Route path={"/achievements"} component={AchievementsIndex} exact />
              <Route path={"/ptm/reschedule"} component={ReschedulePTM} />
              <Route path={"/newReferral"} component={Referal} exact />
              <Route path={"/feedback/:token"} component={PtmFeedBack} exact />
              <Route
                path={"/batch/schedule/:token"}
                component={ChangeBatchSchedule}
                exact
              />
              <Route
                path="/previewFile/:id"
                component={PreviewAlchemyFile}
                exact
              />
              <Route path="/weeklyRecap/:uuid" component={WeeklyReport} exact />
              <Route path="/class-summary" component={ClassSummary} exact />

              <Route path="/referrals/redeem-rewards">
                <Authentication>
                  <ProtectedRoute />
                </Authentication>
              </Route>

              {window.ReactNativeWebView && (
                <Route path={"/native"} component={NativeComponent} exact />
              )}
              <Route path={"/salesforce-chat"} component={SalesforceChat} exact />
              <Route
                path="/teachers-day-post"
                component={TeachersDayPostPage}
                exact
              />
              <Route
                path="/payment/webinar/status/:type"
                component={WebinarPaymentStatusPage}
              />
              <Route
                path="/confirm-demo"
                component={ConfirmDemoAttendance}
                exact
              />
              <Route path="/loop-action" component={LoopAction} exact />
              <Route path="/flags-debug" component={FeatureFlagsDebug} exact />

              <Authentication>
                <ProtectedRoute />
              </Authentication>
              <Route path="/:type/:id" component={StudentCommunity} exact />
              <Redirect to="/" />
            </Switch>
          </Suspense>
        </FeatureFlagProvider>
      </Router>
      {/* Non-closable popup shown by the paid-access guard for unpaid students:
          10s countdown to Sandbox, with an always-available Logout option. */}
      <RedirectToSandboxModal />
    </Fragment>
  );
}

export default Sentry.withProfiler(App);
